import { Box, IconButton, Alert, CircularProgress, Link } from "@mui/joy";
import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { LayoutContext } from "../context";

import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import CachedRoundedIcon from '@mui/icons-material/CachedRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import StyleRoundedIcon from '@mui/icons-material/StyleRounded';

import PageHeader from "../components/ui/PageHeader";
import PageContainer from "../components/ui/PageContainer";
import EmptyState from "../components/ui/EmptyState";
import { APIGetMatches, APIConfirmMutualLove, APICreateMatchChat } from "../api";
import MatchListItem from "../components/MatchComponents/MatchListItem";
import DateTreatBanner from "../components/DateTreatBanner";

/**
 * Симпатии.
 *
 * Запросы и обработчики прежние. Переделано оформление: карточки идут
 * адаптивной сеткой (телефон — одна колонка, планшет — две, ПК — сколько
 * влезет), вместо пустого экрана — понятная плашка, вместо пустоты при
 * загрузке — скелетоны.
 */
export default function MatchPage() {
  const { token, setModalSuggestion } = useContext(LayoutContext);
  const navigate = useNavigate();

  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadMatches();
  }, [token]);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const response = await APIGetMatches(token);
      setMatches(response.matches || []);
    } catch (err) {
      setError('Ошибка загрузки метчей');
      console.error('Error loading matches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatch = async (interactionId) => {
    try {
      setProcessing(true);
      await APIConfirmMutualLove(token, interactionId);
      await loadMatches();
    } catch (err) {
      setError('Ошибка подтверждения симпатии');
      console.error('Error confirming match:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateChat = async (interactionId) => {
    try {
      setProcessing(true);
      await APICreateMatchChat(token, interactionId);
      await loadMatches();
    } catch (err) {
      setError('Ошибка создания чата');
      console.error('Error creating chat:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleSelectMatch = (match) => {
    setSelectedMatch(match);
  };

  const mutualCount = matches.filter(m => m.match_status === 'mutual' || m.chat_created).length;
  const waitingCount = matches.filter(m => m.match_status === 'liked_you').length;

  const subtitle = loading
    ? 'Загружаем…'
    : matches.length === 0
      ? 'Пока пусто'
      : [
          mutualCount ? `${mutualCount} взаимных` : null,
          waitingCount ? `${waitingCount} ждут ответа` : null,
        ].filter(Boolean).join(' • ') || `${matches.length} анкет`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        title="Симпатии"
        subtitle={subtitle}
        actions={
          <IconButton
            color="primary"
            variant="soft"
            onClick={loadMatches}
            disabled={loading}
            aria-label="Обновить"
            sx={{ borderRadius: '14px' }}
          >
            <CachedRoundedIcon />
          </IconButton>
        }
      />

      <PageContainer>
        {error && (
          <Alert color="danger" variant="soft" sx={{ mb: 2, borderRadius: '16px' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2.5 }}>
          <DateTreatBanner />
        </Box>

        {loading ? (
          <Box className="lvc-grid">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <Box key={i} className="lvc-skeleton" sx={{ aspectRatio: '4 / 5.9' }} />
            ))}
          </Box>
        ) : matches.length === 0 ? (
          <EmptyState
            icon={<FavoriteRoundedIcon />}
            title="Взаимных симпатий пока нет"
            text="Листайте ленту и ставьте лайки — как только симпатия окажется взаимной, анкета появится здесь."
            action={
              <button type="button" className="lvc-btn lvc-btn--primary" onClick={() => navigate('/lenta')}>
                <StyleRoundedIcon sx={{ fontSize: 17 }} />
                Перейти в ленту
              </button>
            }
          />
        ) : (
          <Box className="lvc-grid">
            {matches.map((match, index) => (
              <MatchListItem
                key={match.interaction_id || index}
                index={index}
                match={match}
                selected={selectedMatch?.interaction_id === match.interaction_id}
                onSelect={handleSelectMatch}
                onConfirmMatch={handleConfirmMatch}
                onCreateChat={handleCreateChat}
              />
            ))}
          </Box>
        )}

        <Link
          fontSize={13}
          sx={{ mt: 3, width: 'fit-content', color: 'text.tertiary' }}
          component="a"
          startDecorator={<SettingsSuggestRoundedIcon sx={{ fontSize: 17 }} />}
          onClick={() => setModalSuggestion({ open: true })}
        >
          Предложение по улучшению
        </Link>
      </PageContainer>

      {processing && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(43, 29, 58, 0.45)',
            backdropFilter: 'blur(3px)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
}
